import { useCallback, useEffect, useMemo, useRef } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { PageContainer } from "@volca/ui/components/ui/page-components";
import { Stack } from "@volca/ui/components/ui/stack";
import { useReviewQueueFilters } from "@/accounts-payable/ap-url-state";
import {
  DocumentReviewActions,
  DocumentReviewFallback,
  DocumentReviewPreview,
  readInvoiceListReturnPath,
} from "@/accounts-payable/common";
import { useGetInvoice, useGetReviewQueue } from "@/accounts-payable/query";
import {
  AP_TAB,
  AP_TAB_LABEL,
  apListPath,
  flattenPages,
  invoiceReviewPath,
  parseApTab,
  stFilingTarget,
} from "@/accounts-payable/utils";
import { useBreadcrumbs } from "@/app-shell";
import { useBusiness, useBusinessInUrl } from "@/core/business";
import { InvoiceReviewHeader } from "./invoice-review-header";
import {
  findFirstNewQueueItem,
  getCarouselKeyboardDirection,
  ReviewQueueCarousel,
} from "./review-queue-carousel";

const AccountsPayableInvoicePage = () => {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const navigate = useNavigate();
  const { businessId } = useBusiness();
  useBusinessInUrl();
  const location = useLocation();
  const returnPath = readInvoiceListReturnPath(location.state, businessId);
  // Which tab opened this page, per `?tab=`. Absent means we can't know (direct
  // link, bookmark), which reads as "no list context" rather than a guess.
  const [searchParams] = useSearchParams();
  const fromTab = parseApTab(searchParams.get("tab"));
  const fromReviewQueue = fromTab === AP_TAB.review;

  const {
    data: invoice,
    isLoading,
    error,
    isFetching,
    refetch,
  } = useGetInvoice(businessId, invoiceId);
  // Carousel pills cover the review queue pages loaded so far; "next" past the
  // last pill fetches the next page. The carousel is designed to step through
  // the review queue; it doesn't render when arriving from any other tab.
  const { apiFilters } = useReviewQueueFilters();
  const queue = useGetReviewQueue(
    fromReviewQueue ? businessId : null,
    false,
    apiFilters,
  );
  const { fetchNextPage: fetchNextQueuePage } = queue;
  const queueItems = useMemo(
    () => flattenPages(queue.data?.pages),
    [queue.data],
  );
  const orderedIds = useMemo(
    () => queueItems.map((item) => item.id),
    [queueItems],
  );

  const currentIndex = invoiceId ? orderedIds.indexOf(invoiceId) : -1;
  const prevId = currentIndex > 0 ? orderedIds[currentIndex - 1] : null;
  const nextId =
    currentIndex >= 0 && currentIndex < orderedIds.length - 1
      ? orderedIds[currentIndex + 1]
      : null;
  const canGoNext =
    !!nextId || (currentIndex === orderedIds.length - 1 && queue.hasNextPage);

  const goTo = useCallback(
    // Carry the source tab along, so stepping through the queue keeps both the
    // carousel and the back destination.
    (id: string) =>
      navigate(invoiceReviewPath(id, fromTab, location.search), {
        state: location.state,
      }),
    [navigate, fromTab, location.search, location.state],
  );

  const goNext = useCallback(async () => {
    if (nextId) {
      void goTo(nextId);
      return;
    }
    if (!canGoNext || queue.isFetchingNextPage) return;
    const result = await fetchNextQueuePage();
    // fetchNextPage resolves with the *existing* pages on error, and an
    // offset shift can make the new page open with already-loaded items —
    // only navigate to a genuinely new item, otherwise stay put.
    const nextItem = findFirstNewQueueItem(result.data?.pages, orderedIds);
    if (nextItem) void goTo(nextItem.id);
  }, [
    nextId,
    canGoNext,
    queue.isFetchingNextPage,
    fetchNextQueuePage,
    goTo,
    orderedIds,
  ]);

  // Arrow keys flip between invoices, except while typing in a field.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const direction = getCarouselKeyboardDirection(e.key, e.target);
      if (direction === "previous" && prevId) {
        e.preventDefault();
        void goTo(prevId);
      }
      if (direction === "next" && canGoNext) {
        e.preventDefault();
        void goNext();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [prevId, canGoNext, goNext, goTo]);

  // Changing invoice (incl. via the foot carousel) returns you to the top so
  // you start at the header, not wherever you'd scrolled to.
  const topRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    topRef.current?.scrollIntoView({ block: "start" });
  }, [invoiceId]);

  useBreadcrumbs([
    { label: "Accounts payable", href: apListPath() },
    {
      label: AP_TAB_LABEL[fromTab ?? AP_TAB.review],
      href: returnPath ?? apListPath(fromTab, location.search),
    },
    {
      label: invoice?.invoice_number
        ? `Invoice ${invoice.invoice_number}`
        : "Invoice",
    },
  ]);

  const carousel =
    // `currentIndex >= 0` guards a stale `?tab=review` link whose invoice has
    // since left the queue
    fromReviewQueue && currentIndex >= 0 && queueItems.length > 1 ? (
      <ReviewQueueCarousel
        items={queueItems}
        currentIndex={currentIndex}
        hasNextPage={queue.hasNextPage}
        isFetchingNextPage={queue.isFetchingNextPage}
        onSelect={goTo}
        onPrevious={() => prevId && goTo(prevId)}
        onNext={() => void goNext()}
      />
    ) : null;

  if (!invoice) {
    return (
      <PageContainer>
        <DocumentReviewFallback
          documentType="invoice"
          loading={isLoading}
          error={error}
          retrying={isFetching}
          onRetry={() => void refetch()}
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer ref={topRef}>
      {/* The title first; the queue carousel is the header's action. */}
      <InvoiceReviewHeader
        invoice={invoice}
        businessId={businessId}
        actions={carousel}
      />

      {/* PDF + actions */}
      <Stack
        align="start"
        gap={0}
        className="grid w-full grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_480px]"
      >
        <DocumentReviewPreview document={invoice} />

        <DocumentReviewActions
          documentId={invoice.id}
          documentType="invoice"
          documentHasJob={stFilingTarget(invoice) != null}
          businessId={businessId}
        />
      </Stack>

      {/* Carousel repeated at the foot so you can advance without scrolling up. */}
      {carousel && (
        <Stack
          className="w-full"
          align="start"
          gap={4}
          direction="row"
          justify="center"
        >
          {carousel}
        </Stack>
      )}
    </PageContainer>
  );
};

export default AccountsPayableInvoicePage;
